import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Title,
  Text,
  Badge,
  Group,
  Button,
  Table,
  Drawer,
  Stack,
  Tabs,
  LoadingOverlay,
  Modal,
  Textarea,
  TextInput,
  Divider,
  Center,
  Pagination,
} from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

const ADMIN_API = `${API_BASE_URL}/`;
const getAdminHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const STATUS_COLOR = { PENDING: "orange", PROCESSED: "green", REJECTED: "red" };

// "YYYY-MM" for the month just before now — the settlement cron's most
// recently generated period, so it's the sensible default to load.
const defaultReportMonth = () => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

const formatInr = (amount) => `₹${Number(amount).toLocaleString("en-IN")}`;

function WithdrawalRequestsTab() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusTab, setStatusTab] = useState("PENDING");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const [reviewOpen, setReviewOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [actionOpen, setActionOpen] = useState(false);
  const [actionTarget, setActionTarget] = useState(null); // "PROCESSED" | "REJECTED"
  const [payoutNote, setPayoutNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const { showSuccess, showError } = useNotification();

  const fetchWithdrawals = async (page = 1) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ status: statusTab, page, limit: 10 });
      const res = await fetch(`${ADMIN_API}hubs/admin/withdrawals?${query}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setWithdrawals(data.data.withdrawals);
        setPagination({
          page: data.data.pagination.page,
          totalPages: data.data.pagination.totalPages,
        });
      } else {
        showError("Error", data.error?.message || "Failed to load withdrawal requests.");
      }
    } catch (e) {
      showError("Error", "Network error while loading withdrawal requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab]);

  const openReview = (w) => {
    setSelected(w);
    setReviewOpen(true);
  };

  const closeAll = () => {
    setReviewOpen(false);
    setActionOpen(false);
    setSelected(null);
    setActionTarget(null);
    setPayoutNote("");
  };

  const openAction = (target) => {
    setActionTarget(target);
    setActionOpen(true);
  };

  const submitAction = async () => {
    if (!selected || !actionTarget) return;
    setActionLoading(true);
    try {
      const endpoint = actionTarget === "PROCESSED" ? "process" : "reject";
      const res = await fetch(
        `${ADMIN_API}hubs/admin/withdrawals/${selected.id}/${endpoint}`,
        {
          method: "POST",
          headers: getAdminHeaders(),
          body: JSON.stringify({ payoutNote: payoutNote || undefined }),
        },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess(
          actionTarget === "PROCESSED" ? "Marked as paid" : "Rejected",
          actionTarget === "PROCESSED"
            ? `${formatInr(selected.amountInr)} withdrawal marked as paid out.`
            : "The withdrawal request has been rejected.",
        );
        closeAll();
        fetchWithdrawals(pagination.page);
      } else if (data.error?.code === "WITHDRAWAL_ALREADY_ACTIONED") {
        showError("Already actioned", "Someone else already processed or rejected this request.");
        closeAll();
        fetchWithdrawals(pagination.page);
      } else {
        showError("Action failed", data.error?.message || "Something went wrong.");
      }
    } catch (e) {
      showError("Error", "Network error while updating this withdrawal.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Paper withBorder radius="lg" shadow="sm" pos="relative">
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

        <Tabs value={statusTab} onChange={setStatusTab} color="blue" radius="md">
          <Tabs.List px="md" pt="md">
            <Tabs.Tab value="PENDING"><Text size="md" fw={600}>Pending</Text></Tabs.Tab>
            <Tabs.Tab value="PROCESSED"><Text size="md" fw={600}>Processed</Text></Tabs.Tab>
            <Tabs.Tab value="REJECTED"><Text size="md" fw={600}>Rejected</Text></Tabs.Tab>
          </Tabs.List>

          <Box p="md" mih={400}>
            <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover>
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Community</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Requested By</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Amount</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Requested</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Status</Text></Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {withdrawals.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6} ta="center" c="dimmed" py="xl">
                      <Text size="lg">No withdrawal requests in this queue.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  withdrawals.map((w) => (
                    <Table.Tr key={w.id}>
                      <Table.Td><Text fw={700} size="md">{w.hub?.name || w.hubId}</Text></Table.Td>
                      <Table.Td><Text size="sm">{w.requestedBy?.nickname || "Unknown"}</Text></Table.Td>
                      <Table.Td><Text fw={600} size="sm">{formatInr(w.amountInr)}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm">{new Date(w.createdAt).toLocaleDateString()}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLOR[w.status]} variant="light" size="md">
                          {w.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          variant="light"
                          size="sm"
                          leftSection={<IconEye size={16} />}
                          onClick={() => openReview(w)}
                        >
                          Review
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Tabs>

        <Center p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
          <Pagination
            total={pagination.totalPages}
            value={pagination.page}
            onChange={(p) => fetchWithdrawals(p)}
          />
        </Center>
      </Paper>

      <Drawer
        opened={reviewOpen}
        onClose={closeAll}
        position="right"
        size="lg"
        title={<Title order={3} size="h2">Withdrawal Request</Title>}
        padding="xl"
        overlayProps={{ opacity: 0.5, blur: 4 }}
      >
        {selected && (
          <Stack gap="xl">
            <Paper withBorder p="lg" radius="md" bg="gray.0">
              <Text size="sm" tt="uppercase" c="dimmed" fw={700} mb="sm">
                Amount Requested
              </Text>
              <Title order={2} size="h1">{formatInr(selected.amountInr)}</Title>
              <Badge variant="dot" color={STATUS_COLOR[selected.status]} size="md" mt={4}>
                {selected.status}
              </Badge>
            </Paper>

            <Box>
              <Title order={4} size="h3" mb="sm">Community</Title>
              <Paper withBorder p="lg" radius="md">
                <Text fw={600}>{selected.hub?.name || selected.hubId}</Text>
              </Paper>
            </Box>

            <Box>
              <Title order={4} size="h3" mb="sm">Requested By</Title>
              <Paper withBorder p="lg" radius="md">
                <Text fw={600}>{selected.requestedBy?.nickname || "Unknown"}</Text>
                <Text size="sm" c="dimmed">{new Date(selected.createdAt).toLocaleString()}</Text>
              </Paper>
            </Box>

            {selected.status !== "PENDING" && (
              <Box>
                <Title order={4} size="h3" mb="sm">Resolution</Title>
                <Paper withBorder p="lg" radius="md">
                  <Text size="sm">
                    <Text span fw={600}>Actioned by:</Text> {selected.processedBy?.nickname || "—"}
                  </Text>
                  {selected.payoutNote && (
                    <Text size="sm" mt={4}>
                      <Text span fw={600}>Note:</Text> {selected.payoutNote}
                    </Text>
                  )}
                </Paper>
              </Box>
            )}

            {selected.status === "PENDING" && (
              <Box mt="auto" pt="xl" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
                <Group grow>
                  <Button
                    size="xl"
                    variant="light"
                    color="red"
                    disabled={actionLoading}
                    onClick={() => openAction("REJECTED")}
                  >
                    Reject
                  </Button>
                  <Button
                    size="xl"
                    color="green"
                    disabled={actionLoading}
                    onClick={() => openAction("PROCESSED")}
                  >
                    Mark as Paid
                  </Button>
                </Group>
              </Box>
            )}
          </Stack>
        )}
      </Drawer>

      {/* Both process and reject collect an optional payoutNote — no Razorpay
          Route/transfer automation exists, so the actual payout happens
          externally (bank transfer etc.); this note is the paper trail. */}
      <Modal
        opened={actionOpen}
        onClose={() => setActionOpen(false)}
        title={
          <Text fw={700}>
            {actionTarget === "PROCESSED" ? "Confirm payout" : "Reject withdrawal"}
          </Text>
        }
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {actionTarget === "PROCESSED"
              ? "Optional — e.g. bank reference or transfer date."
              : "Optional — reason for rejecting this request."}
          </Text>
          <Textarea
            placeholder={
              actionTarget === "PROCESSED" ? "e.g. Paid via NEFT on 31 Jul." : "e.g. Bank details mismatch."
            }
            minRows={3}
            value={payoutNote}
            onChange={(e) => setPayoutNote(e.target.value)}
          />
          <Divider />
          <Group grow>
            <Button variant="default" onClick={() => setActionOpen(false)}>
              Cancel
            </Button>
            <Button
              color={actionTarget === "PROCESSED" ? "green" : "red"}
              loading={actionLoading}
              onClick={submitAction}
            >
              {actionTarget === "PROCESSED" ? "Confirm Paid" : "Confirm Rejection"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function MonthlyReportsTab() {
  const [month, setMonth] = useState(defaultReportMonth());
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const { showError } = useNotification();

  const fetchReports = async (page = 1) => {
    if (!/^\d{4}-\d{2}$/.test(month)) {
      showError("Invalid month", "Enter a month in YYYY-MM format.");
      return;
    }
    setLoading(true);
    try {
      const query = new URLSearchParams({ month, page, limit: 20 });
      const res = await fetch(`${ADMIN_API}hubs/admin/earnings/monthly-report?${query}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setReports(data.data.reports);
        setPagination({
          page: data.data.pagination.page,
          totalPages: data.data.pagination.totalPages,
        });
      } else {
        showError("Error", data.error?.message || "Failed to load the settlement report.");
      }
    } catch (e) {
      showError("Error", "Network error while loading the settlement report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack gap="md">
      <Paper p="sm" radius="md" withBorder>
        <Group grow>
          <TextInput
            label="Period"
            placeholder="YYYY-MM"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchReports(1)}
          />
          <Button variant="light" mt={22} onClick={() => fetchReports(1)} w={100}>
            Load
          </Button>
        </Group>
      </Paper>

      <Paper withBorder radius="lg" shadow="sm" pos="relative">
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

        <Box p="md" mih={300}>
          <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover>
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Community</Text></Table.Th>
                <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Period</Text></Table.Th>
                <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Earned This Period</Text></Table.Th>
                <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Outstanding Balance</Text></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {reports.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4} ta="center" c="dimmed" py="xl">
                    <Text size="lg">No settlement report for this period.</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                reports.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td><Text fw={700} size="md">{r.hub?.name || r.hubId}</Text></Table.Td>
                    <Table.Td><Text size="sm">{r.periodMonth}</Text></Table.Td>
                    <Table.Td><Text size="sm">{formatInr(r.earnedInThisPeriod)}</Text></Table.Td>
                    <Table.Td><Text fw={600} size="sm">{formatInr(r.outstandingBalance)}</Text></Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>

        <Center p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
          <Pagination
            total={pagination.totalPages}
            value={pagination.page}
            onChange={(p) => fetchReports(p)}
          />
        </Center>
      </Paper>
    </Stack>
  );
}

export default function EarningsSettlementReview() {
  const [view, setView] = useState("withdrawals");

  return (
    <Box className="space-y-6">
      <Group justify="space-between">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Earnings & Settlements
          </Text>
          <Text size="sm" c="dimmed">
            Review creator withdrawal requests and browse monthly settlement reports.
            Payouts happen externally — this only tracks the request and the paper trail.
          </Text>
        </Box>
      </Group>

      <Tabs value={view} onChange={setView} color="blue" radius="md" variant="pills">
        <Tabs.List>
          <Tabs.Tab value="withdrawals">Withdrawal Requests</Tabs.Tab>
          <Tabs.Tab value="reports">Monthly Reports</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {view === "withdrawals" ? <WithdrawalRequestsTab /> : <MonthlyReportsTab />}
    </Box>
  );
}
